// MeteringAlertsPage — every instance that stopped reporting, across the fleet.
//
// This was a card at the top of All Customers, and it had to move. The sweep
// raises one alert per instance per silent hour and nothing resolves them but
// the missing event arriving, so the count grows without bound while the
// instances stay quiet. Within a day of registering the deployed estate it was
// pushing the customer list off the screen — an unbounded list had been placed
// above a bounded one, which is a layout that always loses eventually.
//
// It reads the SAME endpoint All Customers reads. That is deliberate rather
// than lazy: `metricsiq/customers` already returns `open_alerts`, the audit
// trail records one read per model per screen, and giving this page an
// endpoint of its own would add a sixth audited read for rows the operator
// already had. One screen, one reason, no new door.
//
// Quantities and hours are rendered from their strings without becoming
// numbers — see data/format.ts.

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';

import { ScreenFailed, ScreenLoading } from '../components/ScreenState';
import { usageHourLabel } from '../data/format';
import { when } from '../data/instants';
import { readAllCustomers } from '../data/acrossCustomers';
import { usePortalRead } from '../data/usePortalRead';
import { useDocumentTitle } from '../../lib/documentTitle';

const TITLE = 'Metering Alerts';

export function MeteringAlertsPage() {
  useDocumentTitle(TITLE);
  const screen = usePortalRead(readAllCustomers);

  if (screen.status === 'loading') {
    return <ScreenLoading title={TITLE} />;
  }
  if (screen.status === 'failed') {
    // Includes the 404 this endpoint answers a caller who may not look. It
    // is a refusal, not a missing page, and it is shown in the service's own
    // words rather than translated into a guess about which of the two it was.
    return <ScreenFailed title={TITLE} message={screen.message} />;
  }

  const alerts = screen.data.open_alerts;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">{TITLE}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          An hour a running instance was expected to account for and did not,
          across every customer, newest first. An alert clears when the
          missing usage event arrives — nothing here is dismissed by hand.
        </p>
      </div>

      <Card
        className={
          alerts.length > 0 ? 'border-warning/30 bg-warning-soft' : undefined
        }
      >
        <CardHeader>
          <CardTitle className="text-base">
            {alerts.length === 0
              ? 'No open alerts'
              : alerts.length === 1
                ? '1 open alert'
                : `${alerts.length} open alerts`}
          </CardTitle>
          <CardDescription>
            {alerts.length === 0
              ? 'Every running instance has accounted for every hour the sweep has looked at.'
              : 'One row per instance per silent hour. An instance that has been quiet for a day appears here twenty-four times.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {alerts.length === 0 ? (
            // A loaded screen with nothing in it, which is the state a
            // healthy fleet is genuinely in — not a failure and not a
            // spinner.
            <p className="px-6 py-8 text-sm text-muted-foreground">
              Nothing to look at.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Alert</TableHead>
                    <TableHead>Instance</TableHead>
                    <TableHead>Silent hour</TableHead>
                    <TableHead>Detected</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((alert) => (
                    <TableRow key={alert.public_id}>
                      <TableCell className="font-mono text-xs">
                        {alert.public_id}
                      </TableCell>
                      <TableCell className="font-medium">
                        {alert.instance}
                      </TableCell>
                      <TableCell>{usageHourLabel(alert.usage_hour)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {when(alert.detected_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
